import { Component, Input, ViewChild, computed, effect, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BudgetNeuComponent } from '../budget-neu/budget-neu.component';
import { DialogService } from 'src/app/services/dialog.service';
import { IProjektkostenAktuell } from 'src/app/types/IProjektkostenAktuell';
import { IProjektkostenPlan } from 'src/app/types/IProjektkostenPlan';
import { DataService } from 'src/app/services/data.service';
import { AppstateService } from 'src/app/services/appstate.service';
import { NavigationService } from 'src/app/services/navigation.service';
import { IProjekt } from 'src/app/types/IProjekt';
import { DxDataGridComponent } from 'devextreme-angular';
import { IBudgetantrag } from 'src/app/types/IBudgetantrag';
import { GridService } from 'src/app/services/controls/grid.service';
import { Pages } from 'src/app/types/infrastructure/enums/Pages';
import { UserService } from 'src/app/services/user.service';

@Component({
	selector: 'app-projekt-details',
	// standalone: true,
	// imports: [],
	templateUrl: './projekt-details.component.html',
	styleUrl: './projekt-details.component.css',
	providers: [GridService]
})
export class ProjektDetailsComponent {

	@Input()
	set projektid(id: number) {
		this.appStateService.projektId.set(id);
		this.appStateService.projektInfoId.set(id);
	}

	@ViewChild("gridKostengruppeAktuell", { static: false })
	public gridKostengruppeAktuell!: DxDataGridComponent;
	@ViewChild("gridKostengruppePlan", { static: false })
	public gridKostengruppePlan!: DxDataGridComponent;
	@ViewChild("gridKostengruppeAbweichung", { static: false })
	public gridKostengruppeAbweichung!: DxDataGridComponent;

	public projekt = computed<IProjekt>(() => this.dataService.projektDetails());
	public kostengruppenAktuell = computed<IProjektkostenAktuell[]>(() => this.dataService.projektKostenAktuell());
	public kostengruppenPlan = computed<IProjektkostenPlan[]>(() => this.dataService.projektKostenPlan().filter(x => !x.isHiddenInView));
	public kostengruppenAbweichung = computed<IProjektkostenPlan[]>(() => this.dataService.projektKostenAbweichung().filter(x => !x.isHiddenInView));
	public kostengruppenAbweichungSumme = computed<number>(() => this.kostengruppenAbweichung().filter(x => x.isSummary)[0].kosten);

	readonly dialog = inject(MatDialog);

	constructor(public gridService: GridService,
		private dialogService: DialogService,
		public navigationService: NavigationService,
		private dataService: DataService,
		private appStateService: AppstateService,
		private userService: UserService) {

		effect(() => {
			if (this.kostengruppenAktuell().length == 0) {
				this.gridKostengruppeAktuell.instance.beginCustomLoading("Kostengruppen (Aktuell) werden geladen...");
			} else {
				this.gridKostengruppeAktuell.instance.endCustomLoading();
			}
			if (this.kostengruppenPlan().length == 0) {
				this.gridKostengruppePlan.instance.beginCustomLoading("Kostengruppen (Plan) werden geladen...");
			} else {
				this.gridKostengruppePlan.instance.endCustomLoading();
			}
			if (this.kostengruppenAbweichung().length == 0) {
				this.gridKostengruppeAbweichung.instance.beginCustomLoading("Kostengruppen (Abweichung) werden geladen...");
			} else {
				this.gridKostengruppeAbweichung.instance.endCustomLoading();
			}
		});

	}

	BackButtonClicked() {
		this.navigationService.navigateToProjekte();
	}

	onBudgetantragNeuClick() {
		if (this.projekt().aktiv == false) {
			this.dialogService.showInfoDialog('Für ein inaktives Projekt kann kein neuer Budgetantrag gestellt werden. Setzen Sie zunächst das Projekt auf aktiv', 'Neuer Budgetantrag',250).subscribe();
			return;
		}
		const dialogRef = this.dialog.open(BudgetNeuComponent, {
			height: this.projekt().verwaltungsbereichID == 1 ? '842px' : '524px',
			width: '1200px',
			disableClose: true,
			data: { title: 'Neuer Budgetantrag', isBauControlling: this.projekt().verwaltungsbereichID == 1 },
		});

		dialogRef.afterClosed().subscribe(
			(result: IBudgetantrag): void => {
				if (result) {
					this.dialogService.showMessageSheet('Der Budgetantrag wurde erfolgreich gestellt');
					this.navigationService.navigateToBudgetantragDashboard(this.appStateService.projektId(), result.budgetantragID, Pages.PROJEKTDETAILS);
				}
			}
		);
	}

	onBudgetantragClick() {
		this.navigationService.navigateToBudgetantragDashboard(this.appStateService.projektId(), this.projekt().budgetantragID!, Pages.PROJEKTDETAILS);
	}

	onDataGridRowPrepared(e: any) {
		if (e.rowType === "data") {
			if (e.data['isSummary'] === true) {
				e.rowElement.classList.add("dx-row-summary");
			}
			if (e.data['isProportional'] === true) {
				e.rowElement.children[0].classList.add("dx-cell-indent");
			}
		}
	}

	captionButtonNeu() {
		if (!this.projekt().budgetantragGestellt && this.userService.AllowNewBudgetantrag(this.projekt()))
			return 'Neuer Budgetantrag';
		else
			return '';
	}

	captionButtonAktuell() {
		if (this.projekt().budgetantragGestellt)
			return 'Aktueller Budgetantrag';
		else
			return '';
	}

	captionButtonAktiv() {
		const btn = document.querySelector<HTMLButtonElement>('#customButton3');
		if (btn) {
			btn.style.backgroundColor = this.projekt().aktiv ? '#3a4b3aff' : '#c62828'; // grün/rot
			if (this.userService.AllowNewBudgetantrag(this.projekt()) == false && !this.userService.userInfo()?.isFinanzcontrolling) {
				btn.disabled = true;
			}
		}
		if (this.projekt().aktiv)
			return 'Projekt ist aktiv';
		else
			return 'Projekt ist inaktiv';
	}

	onProjektAktivToggleClick() {
		if (this.projekt().aktiv && this.projekt().budgetantragGestellt) {
			this.dialogService.showInfoDialog('Es existiert ein schwebender Budgetantrag für dieses Projekt. Dieser muss zunächst freigegeben werden, bevor das Projekt auf inaktiv gesetzt werden kann.', 'Projekt inaktivieren',250).subscribe();
			return;
		}
		else if (this.projekt().aktiv && this.kostengruppenAbweichungSumme() != 0) {
			this.dialogService.showInfoDialog('Es gibt noch Soll-Ist Abweichungen. Bitte reichen Sie einen abschließenden Budgetantrag ein, bevor Sie das Projekt inaktivieren.', 'Projekt inaktivieren', 250).subscribe(
				(result: any): void => {
					this.onBudgetantragNeuClick();
				});
			return;
		}
		this.dialogService.showDialog(`Möchten Sie das Projekt "${this.projekt().name}" wirklich ${this.projekt().aktiv ? 'inaktivieren' : 'aktivieren'}?`, 'Projekt Aktiv/Inaktiv', '',250).subscribe(
			(result: any): void => {
				if (result) {
					this.dataService.toggleProjektAktiv(this.projekt().projektID).subscribe((response) => {
						this.projekt().aktiv = !this.projekt().aktiv;
						this.dialogService.showMessageSheet(`Das Projekt "${this.projekt().name}" wurde erfolgreich ${this.projekt().aktiv ? 'aktiviert' : 'inaktiviert'}.`);
					});
				}
			}
		);
	};
}
